import React, { useEffect, useRef, useState } from "react";

type CaptureFeedback = {
  ok: boolean;
  message?: string;
  suggestions?: string[];
  move?: { dx: number; dy: number; rotate: number };
  overlay?: any;
};

type OCRToken = {
  text: string;
  score?: number | null;
  polygon?: number[] | null; // [x1,y1,x2,y2,...]
  rect?: { x: number; y: number; w: number; h: number } | null;
};

export type OCRScanResponse = {
  ok?: boolean;
  notice?: string | null;
  error?: string | null;
  reasons?: string[];
  capture_feedback?: CaptureFeedback;
  ocr_text?: string;
  preview_base64?: string | null;
  ocr_tokens?: OCRToken[];
  ocr_view_size?: { w: number; h: number } | null;
  structured?: Record<string, any>;
};

type Props = {
  apiScanUrl: string;   // e.g. "/api/scan"
  apiGuideUrl: string;  // e.g. "/api/guide"
  onClose: () => void;
  onResult: (data: OCRScanResponse) => void;
};

const guideIntervalMs = 400;

const CameraOCR: React.FC<Props> = ({ apiScanUrl, apiGuideUrl, onClose, onResult }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const overlayRef = useRef<HTMLCanvasElement | null>(null);
  const [banner, setBanner] = useState<string>("Align your ID inside the frame");
  const [busy, setBusy] = useState<boolean>(false);
  const guideTimer = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // start camera on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
          audio: false,
        });
        if (cancelled) return;
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        startGuideLoop();
      } catch (err) {
        console.error(err);
        setBanner("Camera not available; you can still upload a photo.");
      }
    })();

    return () => {
      if (guideTimer.current) window.clearInterval(guideTimer.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  function startGuideLoop() {
    if (guideTimer.current) window.clearInterval(guideTimer.current);
    guideTimer.current = window.setInterval(async () => {
      try {
        const blob = await captureFrameBlob();
        if (!blob) return;
        const fd = new FormData();
        fd.append("file", blob, "frame.jpg");
        const res = await fetch(apiGuideUrl, { method: "POST", body: fd });
        const json = await res.json();
        const fb: CaptureFeedback | undefined = json?.capture_feedback;
        if (!fb) return;
        // Banner text
        const tips = fb.suggestions?.slice(0, 2).join(", ");
        setBanner(fb.ok ? "Looks good" : tips ? `Adjust: ${tips}` : "Adjust framing/lighting");
        // Draw simple “nudges” arrows on overlay
        drawGuideArrows(fb.move ?? { dx: 0, dy: 0, rotate: 0 });
      } catch (e) {
        // ignore intermittent network hiccups
      }
    }, guideIntervalMs);
  }

  async function captureFrameBlob(): Promise<Blob | null> {
    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) return null;
    const c = document.createElement("canvas");
    const maxW = 960;
    const w = Math.min(maxW, video.videoWidth || maxW);
    const h = Math.round((w * (video.videoHeight || 720)) / (video.videoWidth || 1280));
    c.width = w;
    c.height = h;
    const ctx = c.getContext("2d")!;
    ctx.drawImage(video, 0, 0, w, h);
    return await new Promise((resolve) => c.toBlob((b) => resolve(b), "image/jpeg", 0.85));
  }

  function drawGuideArrows(move: { dx: number; dy: number; rotate: number }) {
    const canvas = overlayRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;
    // size overlay to video client size
    const { clientWidth: vw, clientHeight: vh } = video;
    canvas.width = vw;
    canvas.height = vh;
    const ctx = canvas.getContext("2d")!;
    ctx.clearRect(0, 0, vw, vh);

    // Only draw noticeable nudges
    const thresh = 0.15;
    const nudges: string[] = [];
    if (Math.abs(move.dx) > thresh) nudges.push(move.dx > 0 ? "→" : "←");
    if (Math.abs(move.dy) > thresh) nudges.push(move.dy > 0 ? "↑" : "↓");
    if (Math.abs(move.rotate) > 4) nudges.push(move.rotate > 0 ? "⟲" : "⟳");

    if (!nudges.length) return;

    // draw a semi-transparent pill
    const label = nudges.join(" ");
    ctx.font = "20px system-ui";
    const pad = 10;
    const metrics = ctx.measureText(label);
    const bw = metrics.width + pad * 2;
    const bh = 34;
    const x = (vw - bw) / 2;
    const y = 12;

    ctx.fillStyle = "rgba(0,0,0,0.4)";
    ctx.fillRect(x, y, bw, bh);
    ctx.fillStyle = "#fff";
    ctx.fillText(label, x + pad, y + 24);
  }

  async function doScan() {
    if (busy) return;
    setBusy(true);
    try {
      const blob = await captureFrameBlob();
      if (!blob) return;

      const fd = new FormData();
      fd.append("file", blob, "capture.jpg");
      const res = await fetch(apiScanUrl, { method: "POST", body: fd });
      const json: OCRScanResponse = await res.json();

      // Show banner (notice / ok)
      setBanner(json.notice || (json.ok ? "Captured ✅" : "Try again"));

      // Draw OCR boxes over the current overlay (using tokens)
      drawOCRTokens(json.ocr_tokens || [], json.ocr_view_size || null);

      // Bubble the whole payload up to parent
      onResult(json);
    } catch (e: any) {
      setBanner("Scan failed — try again");
    } finally {
      setBusy(false);
    }
  }

  function drawOCRTokens(tokens: OCRToken[], viewSize: { w: number; h: number } | null) {
    const canvas = overlayRef.current;
    const video = videoRef.current;
    if (!canvas || !video || !viewSize) return;

    // size overlay to video client size
    const { clientWidth: vw, clientHeight: vh } = video;
    canvas.width = vw;
    canvas.height = vh;
    const ctx = canvas.getContext("2d")!;
    ctx.clearRect(0, 0, vw, vh);

    const scaleX = vw / (viewSize.w || 1);
    const scaleY = vh / (viewSize.h || 1);

    for (const t of tokens) {
      const score = typeof t.score === "number" ? t.score : 0.5;
      const color =
        score < 0.6 ? "rgba(255,0,0,0.95)" : score < 0.85 ? "rgba(255,165,0,0.95)" : "rgba(0,180,0,0.95)";
      const lineW = Math.max(2, Math.round(vw / 240));
      ctx.strokeStyle = color;
      ctx.lineWidth = lineW;

      if (t.polygon && t.polygon.length >= 8) {
        const pts: [number, number][] = [];
        for (let i = 0; i < t.polygon.length; i += 2) {
          pts.push([t.polygon[i] * scaleX, t.polygon[i + 1] * scaleY]);
        }
        ctx.beginPath();
        ctx.moveTo(pts[0][0], pts[0][1]);
        for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
        ctx.closePath();
        ctx.stroke();
      } else if (t.rect) {
        const x = t.rect.x * scaleX;
        const y = t.rect.y * scaleY;
        const w = t.rect.w * scaleX;
        const h = t.rect.h * scaleY;
        ctx.strokeRect(x, y, w, h);
      }
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.8)",
        display: "grid",
        placeItems: "center",
        zIndex: 9999,
      }}
    >
      <div style={{ width: "min(92vw, 680px)" }}>
        <div style={{ color: "#fff", fontSize: 18, marginBottom: 8 }}>{banner}</div>

        <div style={{ position: "relative" }}>
          <video
            ref={videoRef}
            playsInline
            muted
            autoPlay
            style={{ width: "100%", borderRadius: 8, transform: "scaleX(1)" /* mirror if needed */ }}
          />
          <canvas
            ref={overlayRef}
            style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
          />
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button
            onClick={doScan}
            disabled={busy}
            style={{
              padding: "10px 14px",
              borderRadius: 8,
              border: "1px solid #1d4ed8",
              background: busy ? "#93c5fd" : "#2563eb",
              color: "#fff",
              minWidth: 120,
            }}
          >
            {busy ? "Scanning…" : "Scan"}
          </button>
          <button
            onClick={onClose}
            style={{
              padding: "10px 14px",
              borderRadius: 8,
              border: "1px solid #ddd",
              background: "#111",
              color: "#fff",
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CameraOCR;

